import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { contactData } from '../data/mock';
import { toast } from 'sonner';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success('Message sent! Betty will get back to you soon.');
    setFormData({ name: '', email: '', phone: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section className="contact-section py-20 md:py-32 relative">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            {contactData.title}
          </h2>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto opacity-80">
            {contactData.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          <div className="contact-info-card rounded-3xl p-8 md:p-10">
            <h3 className="text-3xl font-bold mb-8">Get In Touch</h3>
            
            <div className="space-y-6">
              <div className="contact-info-item">
                <div className="contact-icon-wrapper">
                  <Phone className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-sm font-semibold uppercase tracking-wider mb-1 opacity-70">
                    Phone
                  </div>
                  <a 
                    href={`tel:${contactData.phone}`}
                    className="text-xl font-semibold hover:underline"
                  >
                    {contactData.phone}
                  </a>
                </div>
              </div>

              <div className="contact-info-item">
                <div className="contact-icon-wrapper">
                  <Mail className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-sm font-semibold uppercase tracking-wider mb-1 opacity-70">
                    Email
                  </div>
                  <a 
                    href={`mailto:${contactData.email}`}
                    className="text-xl font-semibold hover:underline break-all"
                  >
                    {contactData.email}
                  </a>
                </div>
              </div>

              <div className="contact-info-item">
                <div className="contact-icon-wrapper">
                  <MapPin className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-sm font-semibold uppercase tracking-wider mb-1 opacity-70">
                    Location
                  </div>
                  <p className="text-xl font-semibold">
                    {contactData.address}
                  </p>
                </div>
              </div>

              <div className="contact-info-item">
                <div className="contact-icon-wrapper">
                  <Clock className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-sm font-semibold uppercase tracking-wider mb-1 opacity-70">
                    Hours
                  </div>
                  <p className="text-xl font-semibold">
                    {contactData.hours}
                  </p>
                </div>
              </div>

              <div className="contact-info-item">
                <div className="contact-icon-wrapper">
                  <Globe className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-sm font-semibold uppercase tracking-wider mb-1 opacity-70">
                    Websites
                  </div>
                  <div className="space-y-1">
                    <a 
                      href={`https://${contactData.websites.bakery}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-lg font-semibold hover:underline"
                    >
                      {contactData.websites.bakery}
                    </a>
                    <a 
                      href={`https://${contactData.websites.shop}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-lg font-semibold hover:underline"
                    >
                      {contactData.websites.shop}
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-10 pt-10 border-t contact-border">
              <Button 
                size="lg" 
                className="w-full contact-cta text-lg"
                onClick={() => window.location.href = `tel:${contactData.phone}`}
              >
                <Phone className="mr-2 h-5 w-5" />
                Call Now to Order
              </Button>
            </div>
          </div>

          <div className="contact-form-card rounded-3xl p-8 md:p-10">
            <h3 className="text-3xl font-bold mb-8">Send a Message</h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="form-label">
                  Your Name
                </label>
                <Input 
                  id="name"
                  name="name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>

              <div>
                <label htmlFor="email" className="form-label">
                  Email Address
                </label>
                <Input 
                  id="email"
                  name="email"
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                />
              </div>

              <div>
                <label htmlFor="phone" className="form-label">
                  Phone Number
                </label>
                <Input 
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>

              <div>
                <label htmlFor="message" className="form-label">
                  Message
                </label>
                <Textarea 
                  id="message"
                  name="message"
                  placeholder="Tell us about your order or catering needs..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                  className="form-input min-h-[150px]"
                />
              </div>

              <Button type="submit" size="lg" className="w-full form-submit">
                Send Message
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
